import React, { useEffect, useRef, useState } from 'react';
import { connect } from 'react-redux';
import { Alert } from '@syneto/compass-react';
import { GET_FIRMWARE_ADVISORIES_DEFAULT_QUERY_PARAMS } from '../../../../constants/firmware-advisories';
import { getComponentTypes } from '../../../../data/hardwareInventory/actions';
import {
	createFirmwareAdvisory,
	getFirmwareAdvisories,
	hideFirmwareAdvisoryFormModal,
	hideFirmwareAdvisoryRevokeModal,
	revokeFirmwareAdvisory,
	showFirmwareAdvisoryFormModal,
	showFirmwareAdvisoryRevokeModal,
	updateFirmwareAdvisory
} from '../../../../data/firmwareAdvisory/actions';
import FirmwareAdvisoriesTable from './firmwareAdvisoriesTable';
import FirmwareAdvisoryFormModal from './firmwareAdvisoryFormModal';
import FirmwareAdvisoryRevokeModal from './firmwareAdvisoryRevokeModal';

const AdminFirmwareAdvisories = (props) => {
	const {
		advisories,
		pagination,
		advisoriesLoading,
		advisoriesError,
		saving,
		showFormModal,
		editedAdvisory,
		advisorySeed,
		advisoryToRevoke,
		componentTypes,
		componentTypesLoading,
		componentTypesError,
		getFirmwareAdvisories,
		getComponentTypes,
		createFirmwareAdvisory,
		updateFirmwareAdvisory,
		revokeFirmwareAdvisory,
		showFormModalFor,
		hideFormModal,
		showRevokeModalFor,
		hideRevokeModal
	} = props;

	const [queryParams, setQueryParams] = useState(GET_FIRMWARE_ADVISORIES_DEFAULT_QUERY_PARAMS);

	/*
	 * A write outlives its request: it goes on to poll a job for up to a minute. Navigating away
	 * mid-poll has to stop it, otherwise it dispatches into an unmounted page and pushes a
	 * notification about work nobody is watching any more.
	 */
	const abortRef = useRef(null);

	useEffect(() => {
		abortRef.current = new AbortController();

		return () => {
			abortRef.current?.abort();
		};
	}, []);

	/*
	 * The catalog powers the component type picker, the matcher pre-fill and the "seen in the
	 * fleet" panel. Fetched unconditionally rather than reused-if-present: componentTypesLoading
	 * starts true, so a guard that skips the fetch would leave the picker spinning forever on a
	 * fresh store, and a write depends on componentTypeId being a current one.
	 */
	useEffect(() => {
		getComponentTypes();
	}, []);

	useEffect(() => {
		getFirmwareAdvisories(queryParams);
	}, [queryParams]);

	const writeOptions = () => {
		return { queryParams: queryParams, signal: abortRef.current?.signal };
	};

	/*
	 * A narrower filter can leave fewer rows than the current offset, so the page goes back to the
	 * first one - otherwise the table lands on an empty page of a non-empty result.
	 */
	const onStatusChange = (status) => {
		setQueryParams((current) => {
			return { ...current, status: status, start: 0 };
		});
	};

	const onPaginationChange = (start, limit) => {
		setQueryParams((current) => {
			return { ...current, start: start, limit: limit };
		});
	};

	const onSubmitAdvisory = (values) => {
		if (editedAdvisory) {
			return updateFirmwareAdvisory(editedAdvisory.id, values, writeOptions());
		}

		return createFirmwareAdvisory(values, writeOptions());
	};

	const onConfirmRevoke = () => {
		return revokeFirmwareAdvisory(advisoryToRevoke.id, writeOptions());
	};

	return (
		<div id="firmware-advisories" className="p-4 bg-white w-100">
			<div className="col-12">
				{advisoriesError && <Alert variant="danger">{advisoriesError}</Alert>}
				{componentTypesError && <Alert variant="danger">{componentTypesError}</Alert>}

				<FirmwareAdvisoriesTable
					advisories={advisories}
					pagination={pagination}
					componentTypes={componentTypes}
					isLoading={advisoriesLoading}
					hasError={Boolean(advisoriesError)}
					isSaving={saving}
					status={queryParams.status}
					onStatusChange={onStatusChange}
					onPaginationChange={onPaginationChange}
					onAdd={() => { return showFormModalFor(null); }}
					onEdit={showFormModalFor}
					onRevoke={showRevokeModalFor}
				/>

				{showFormModal && (
					<FirmwareAdvisoryFormModal
						advisory={editedAdvisory}
						seed={advisorySeed}
						componentTypes={componentTypes}
						componentTypesLoading={componentTypesLoading}
						onSubmitAdvisory={onSubmitAdvisory}
						onCancel={hideFormModal}
					/>
				)}

				<FirmwareAdvisoryRevokeModal
					advisory={advisoryToRevoke}
					componentTypes={componentTypes}
					isSaving={saving}
					onConfirm={onConfirmRevoke}
					onCancel={hideRevokeModal}
				/>
			</div>
		</div>
	);
};

const mapStateToProps = (state) => {
	return {
		advisories: state.firmwareAdvisories.advisories,
		pagination: state.firmwareAdvisories.pagination,
		advisoriesLoading: state.firmwareAdvisories.advisoriesLoading,
		advisoriesError: state.firmwareAdvisories.advisoriesError,
		saving: state.firmwareAdvisories.saving,
		showFormModal: state.firmwareAdvisories.showFormModal,
		editedAdvisory: state.firmwareAdvisories.editedAdvisory,
		advisorySeed: state.firmwareAdvisories.advisorySeed,
		advisoryToRevoke: state.firmwareAdvisories.advisoryToRevoke,
		/*
		 * Read from the hardware inventory slice rather than fetched into this one: it is the same
		 * unpaginated endpoint, and a second copy could disagree with the fleet dashboard about
		 * which component types exist.
		 */
		componentTypes: state.hardwareInventory.componentTypes,
		componentTypesLoading: state.hardwareInventory.componentTypesLoading,
		componentTypesError: state.hardwareInventory.componentTypesError
	};
};

const mapDispatchToProps = (dispatch) => {
	return {
		getFirmwareAdvisories: (queryParams) => { return dispatch(getFirmwareAdvisories(queryParams)); },
		getComponentTypes: () => { return dispatch(getComponentTypes()); },
		createFirmwareAdvisory: (values, options) => { return dispatch(createFirmwareAdvisory(values, options)); },
		updateFirmwareAdvisory: (id, values, options) => { return dispatch(updateFirmwareAdvisory(id, values, options)); },
		revokeFirmwareAdvisory: (id, options) => { return dispatch(revokeFirmwareAdvisory(id, options)); },
		showFormModalFor: (advisory) => { return dispatch(showFirmwareAdvisoryFormModal(advisory)); },
		hideFormModal: () => { return dispatch(hideFirmwareAdvisoryFormModal()); },
		showRevokeModalFor: (advisory) => { return dispatch(showFirmwareAdvisoryRevokeModal(advisory)); },
		hideRevokeModal: () => { return dispatch(hideFirmwareAdvisoryRevokeModal()); }
	};
};

export default connect(mapStateToProps, mapDispatchToProps)(AdminFirmwareAdvisories);
